import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Search, Plus, Settings, Sparkles, Shield, Pin, PinOff, BellOff, Bell, Trash2, MoreVertical, Users, Pencil, BadgeCheck, CheckCheck } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { getInitials, avatarColor } from "@/lib/avatar";
import LiveRing from "@/components/live/LiveRing";
import { hidePreviewsOn } from "@/lib/prefs";
import api from "@/lib/api";
import { msgType } from "@/lib/uploads";
import { isLottieSticker, stickerPreview } from "@/lib/lottieStickers";
import { toast } from "sonner";

function Avatar({ chat }) {
  const initials = getInitials(chat.title);
  const inner = (
    <div className="relative shrink-0">
      {chat.avatar ? (
        <img src={chat.avatar} alt="" className="h-12 w-12 rounded-2xl object-cover border-2 border-[var(--rc-gold)]" />
      ) : (
        <div className="h-12 w-12 rounded-2xl grid place-items-center text-[var(--rc-gold)] font-semibold text-base tracking-wide border-2 border-[var(--rc-gold)]" style={{ backgroundColor: avatarColor(chat.title) }}>{initials}</div>
      )}
      {chat.type === "direct" && chat.peer?.online && (
        <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-emerald-400 border-2 border-[var(--rc-bg)]" data-testid="online-dot" />
      )}
    </div>
  );
  return chat.type === "direct" && chat.peer?.id ? <LiveRing uid={chat.peer.id}>{inner}</LiveRing> : inner;
}

function fmt(ts) {
  if (!ts) return "";
  const d = dayjs(ts);
  return d.isSame(dayjs(), "day") ? d.format("HH:mm") : d.format("DD/MM");
}

function ChatItem({ chat, active, onSelect, onReload, typing }) {
  const { user } = useAuth();
  const [menu, setMenu] = useState(false);
  const preview = chat.last_message
    ? (hidePreviewsOn() ? "🔒 Message"
      : chat.last_message.deleted ? "🚫 Message deleted"
      : chat.last_message.type === "call" ? `📞 ${chat.last_message.call_meta?.media === "video" ? "Video call" : "Voice call"}`
      : chat.last_message.type === "sticker" && isLottieSticker(chat.last_message.text) ? stickerPreview(chat.last_message.text)
      : chat.last_message.type !== "text" ? `📎 ${msgType(chat.last_message.type)}`
      : chat.last_message.text)
    : "Tap to start chatting";

  // WhatsApp-style read ticks for the LAST message, only when WE sent it.
  const lm = chat.last_message;
  const meId = String(user?.id || "");
  const outgoing = !!lm && !lm.deleted && lm.type !== "call" && String(lm.sender_id) === meId;
  const readBy = (lm?.read_by || []).map(String);
  let seenByOther = false;
  if (outgoing) {
    if (chat.type === "direct") {
      const pid = chat.peer?.id ? String(chat.peer.id) : null;
      seenByOther = !!pid && chat.peer?.read_receipts !== false && readBy.includes(pid);
    } else {
      const others = (chat.members || []).map(String).filter((m) => m !== meId);
      seenByOther = others.length > 0 && others.every((m) => readBy.includes(m));
    }
  }

  const act = async (fn, e) => { e.stopPropagation(); setMenu(false); await fn(); onReload(); };

  return (
    <div onClick={() => onSelect(chat.id)} data-testid={`chat-item-${chat.id}`}
      className={`group relative flex items-center gap-3 px-3 py-3 rounded-2xl cursor-pointer transition-colors ${active ? "bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20" : "hover:bg-[var(--rc-surface)] border border-transparent"}`}>
      <Avatar chat={chat} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate">{chat.title || "Conversation"}</span>
          {chat.type === "direct" && chat.peer?.email_verified && <BadgeCheck size={14} className="text-[var(--rc-gold)] shrink-0" data-testid={`verified-badge-list-${chat.id}`} />}
          {chat.pinned && <Pin size={12} className="text-[var(--rc-gold)] shrink-0" />}
          {chat.muted && <BellOff size={12} className="text-[var(--rc-muted)] shrink-0" />}
          <span className="ml-auto text-xs text-[var(--rc-muted)] shrink-0">{fmt(chat.last_message?.created_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--rc-muted)] truncate flex-1 flex items-center gap-1">
            {typing ? <span className="text-[var(--rc-gold)]">{typing} typing…</span> : (
              <>
                {outgoing && (
                  <CheckCheck size={16} data-testid={`read-ticks-${chat.id}`}
                    className={`shrink-0 ${seenByOther ? "text-[#34B7F1]" : "text-[var(--rc-muted)]"}`} />
                )}
                <span className="truncate">{preview}</span>
              </>
            )}
          </span>
          {chat.unread > 0 && (
            <span className="shrink-0 min-w-5 h-5 px-1.5 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black text-xs font-bold" data-testid={`unread-${chat.id}`}>{chat.unread}</span>
          )}
        </div>
      </div>
      <button onClick={(e) => { e.stopPropagation(); setMenu((m) => !m); }} data-testid={`chat-menu-${chat.id}`}
        className="opacity-0 group-hover:opacity-100 text-[var(--rc-muted)] hover:text-[var(--rc-gold)] transition-opacity">
        <MoreVertical size={18} />
      </button>
      {menu && (
        <>
          <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setMenu(false); }} />
          <div className="absolute right-2 top-12 z-20 w-40 rounded-xl bg-[var(--rc-surface-2)] border border-[var(--rc-border)] py-1 shadow-xl rc-pop">
            <button onClick={(e) => act(() => api.post(`/chats/${chat.id}/pin`), e)} data-testid={`pin-${chat.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--rc-surface)]">
              {chat.pinned ? <PinOff size={15} /> : <Pin size={15} />} {chat.pinned ? "Unpin" : "Pin"}
            </button>
            <button onClick={(e) => act(() => api.post(`/chats/${chat.id}/mute`), e)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[var(--rc-surface)]">
              {chat.muted ? <Bell size={15} /> : <BellOff size={15} />} {chat.muted ? "Unmute" : "Mute"}
            </button>
            <button onClick={(e) => act(async () => { await api.delete(`/chats/${chat.id}`); toast.success("Chat removed"); }, e)} data-testid={`delete-chat-${chat.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-[var(--rc-surface)]">
              <Trash2 size={15} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ChatList({ chats, activeId, onSelect, onReload, onNewChat, onOpenSettings, onOpenAI, onOpenAdmin, typing }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [q, setQ] = useState("");

  const { pinned, others } = useMemo(() => {
    const filtered = chats.filter((c) => (c.title || "").toLowerCase().includes(q.toLowerCase()));
    return { pinned: filtered.filter((c) => c.pinned), others: filtered.filter((c) => !c.pinned) };
  }, [chats, q]);

  return (
    <div className="h-full flex flex-col bg-[var(--rc-bg)] relative">
      <div className="rc-safe-top px-4 pt-5 pb-3 border-b border-[var(--rc-border)] sticky top-0 bg-[var(--rc-bg)]/90 backdrop-blur-xl z-10">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-heading text-4xl md:text-3xl">{t("chats")}</h1>
          <div className="flex items-center gap-2">
            <button onClick={onOpenAI} data-testid="open-ai-btn" title={t("ai_assistant")} className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)] transition-colors hover:bg-[var(--rc-surface-2)]"><Sparkles size={18} /></button>
            <button onClick={onNewChat} data-testid="new-group-btn" title={t("new_chat")} className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)] transition-colors hover:bg-[var(--rc-surface-2)]"><Users size={18} /></button>
            <button onClick={() => document.getElementById("rc-search")?.focus()} data-testid="search-icon-btn" title={t("search")} className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-gold)] transition-colors hover:bg-[var(--rc-surface-2)]"><Search size={18} /></button>
            {user?.role === "admin" && (
              <button onClick={onOpenAdmin} data-testid="open-admin-btn" title={t("admin")} className="hidden md:grid h-10 w-10 place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-silver)]"><Shield size={18} /></button>
            )}
            <button onClick={onOpenSettings} data-testid="open-settings-btn" title={t("settings")} className="hidden md:grid h-10 w-10 place-items-center rounded-full bg-[var(--rc-surface)] text-[var(--rc-silver)]"><Settings size={18} /></button>
          </div>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--rc-muted)]" />
          <input id="rc-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} data-testid="chat-search-input"
            className="w-full bg-[var(--rc-surface)] border border-[var(--rc-border)] rounded-full pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[var(--rc-gold)]/50" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 no-scrollbar">
        {chats.length === 0 && (
          <div className="text-center py-20 px-6">
            <p className="font-heading text-2xl mb-2">{t("no_chats")}</p>
            <p className="text-[var(--rc-muted)] text-sm mb-6">{t("start_conversation")}</p>
            <button onClick={onNewChat} className="inline-flex items-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-5 py-2.5" data-testid="empty-new-chat-btn"><Plus size={16} /> {t("new_chat")}</button>
          </div>
        )}
        {pinned.length > 0 && (
          <>
            <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[var(--rc-gold)] flex items-center gap-1.5"><Pin size={12} /> {t("pinned")}</p>
            {pinned.map((c) => <ChatItem key={c.id} chat={c} active={c.id === activeId} onSelect={onSelect} onReload={onReload} typing={typing[c.id]} />)}
          </>
        )}
        {others.length > 0 && (
          <>
            {pinned.length > 0 && <p className="px-3 py-2 mt-2 text-xs font-semibold uppercase tracking-wider text-[var(--rc-muted)]">{t("all_chats")}</p>}
            {others.map((c) => <ChatItem key={c.id} chat={c} active={c.id === activeId} onSelect={onSelect} onReload={onReload} typing={typing[c.id]} />)}
          </>
        )}
      </div>

      {/* Floating compose button */}
      <button onClick={onNewChat} data-testid="new-chat-btn" title={t("new_chat")}
        className="absolute bottom-6 right-6 h-14 w-14 grid place-items-center rounded-2xl bg-[var(--rc-gold)] text-black shadow-lg hover:bg-[var(--rc-gold-light)] hover:-translate-y-0.5 transition-transform"
        style={{ boxShadow: "0 0 24px rgba(212,175,55,0.4)" }}>
        <Pencil size={22} />
      </button>
    </div>
  );
}
